// src/components/AI/RyderVision.js
import React, { useState } from 'react';
import { Loader, Eye, CheckCircle, AlertCircle } from 'lucide-react';
import FileDropZone from '../FileUpload/FileDropZone';

const RyderVision = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [ryderStatus, setRyderStatus] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  // Check Ryder status
  const checkRyderStatus = async () => {
    try {
      const response = await fetch('http://100.96.19.46:11434/api/tags');
      const data = await response.json();
      
      const hasRyder = data.models.some(m => m.name.includes('ryder') || m.name.includes('qwen'));
      
      setRyderStatus({
        online: true,
        model: hasRyder ? 'Qwen3 32B' : 'Unknown',
        ready: hasRyder
      });
      setError(null);
    } catch (err) {
      setRyderStatus({ online: false, ready: false });
      setError('Cannot connect to Ryder AI');
    }
  };

  React.useEffect(() => {
    checkRyderStatus();
  }, []);

  const handleFileAnalyzed = async (fileData) => {
    setIsProcessing(true);
    setError(null);
    
    try {
      // Send to your backend API which will route to Ryder
      const response = await fetch('/api/ryder/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: fileData.name,
          fileContent: fileData.content,
          fileType: fileData.type
        })
      });

      if (!response.ok) throw new Error('Analysis failed');

      const result = await response.json();
      setAnalysisResult(result);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '24px'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f120, #8b5cf620)',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ color: '#6366f1', margin: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Eye size={28} />
              Ryder Vision AI
            </h2>
            <p style={{ color: '#94a3b8', margin: 0, fontSize: '14px' }}>
              Powered by Qwen3 32B - Advanced document and image analysis
            </p>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {ryderStatus?.online ? (
              <>
                <CheckCircle size={16} style={{ color: '#22c55e' }} />
                <span style={{ color: '#22c55e', fontSize: '12px', fontWeight: '500' }}>
                  {ryderStatus.model} Online
                </span>
              </>
            ) : (
              <>
                <AlertCircle size={16} style={{ color: '#ef4444' }} />
                <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: '500' }}>
                  Offline
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* File Drop Zone */}
      <FileDropZone 
        onFileAnalyzed={handleFileAnalyzed}
        style={{
          minHeight: '200px',
          background: 'rgba(99, 102, 241, 0.05)',
          border: '2px dashed rgba(99, 102, 241, 0.3)'
        }}
      />

      {/* Processing Indicator */}
      {isProcessing && (
        <div style={{
          textAlign: 'center',
          padding: '32px',
          background: 'rgba(99, 102, 241, 0.1)',
          borderRadius: '12px',
          marginTop: '16px'
        }}>
          <Loader size={32} className="animate-spin" style={{ color: '#6366f1', marginBottom: '16px' }} />
          <p style={{ color: '#6366f1', fontWeight: '500', fontSize: '16px' }}>
            Ryder is analyzing your file...
          </p>
        </div>
      )}

      {/* Analysis Result */}
      {analysisResult && !isProcessing && (
        <div style={{
          marginTop: '24px',
          background: 'rgba(15, 23, 42, 0.8)',
          border: '1px solid rgba(99, 102, 241, 0.3)',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{ color: '#6366f1', marginBottom: '16px' }}>
            Analysis Results
          </h3>
          <div style={{
            color: '#e2e8f0',
            fontSize: '14px',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap'
          }}>
            {analysisResult.analysis || analysisResult.response}
          </div>
        </div>
      )}

      {/* Capabilities Info */}
      <div style={{
        marginTop: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px'
      }}>
        {[
          { icon: '📄', title: 'Document Analysis', desc: 'PDFs, Word docs, text files' },
          { icon: '🖼️', title: 'Image Recognition', desc: 'Photos, screenshots, diagrams' },
          { icon: '📊', title: 'Data Extraction', desc: 'Tables, forms, structured data' },
          { icon: '🔍', title: 'Detail Detection', desc: 'OCR, handwriting, annotations' }
        ].map((cap, i) => (
          <div key={i} style={{
            padding: '16px',
            background: 'rgba(99, 102, 241, 0.05)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>{cap.icon}</div>
            <div style={{ color: '#e2e8f0', fontWeight: '500', marginBottom: '4px', fontSize: '14px' }}>
              {cap.title}
            </div>
            <div style={{ color: '#94a3b8', fontSize: '12px' }}>
              {cap.desc}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RyderVision;
